import React, { useEffect, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useReportes } from './ReportesContex';
import { Gauge, Clock } from 'tabler-icons-react';
import '../../styles/reportes.css';

function getPrimaryColor() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--color').trim();
    return raw.startsWith('#') ? raw.slice(1) : raw;
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    const [r1, g1, b1] = parse(hex.padStart(6, '0'));
    const [r2, g2, b2] = parse(blendWith.padStart(6, '0'));
    const r = Math.round(r1 * ratio + r2 * (1 - ratio));
    const g = Math.round(g1 * ratio + g2 * (1 - ratio));
    const b = Math.round(b1 * ratio + b2 * (1 - ratio));
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

async function exportarExcel(indicadores, selectedArea, selectedLinea) {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');

    const primary  = getPrimaryColor();
    const headerBg = blendHex(primary, '000000', 0.8);
    const infoBg   = blendHex(primary, 'ffffff', 0.85);

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Indicadores');

    const toArgb = (hex) => `FF${hex.toUpperCase()}`;
    const hStyle = (text, bg) => ({
        value: text,
        style: {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(bg) } },
            alignment: { horizontal: 'center', vertical: 'middle' },
        },
    });

    const infoText = [
        selectedArea?.nombre  ? `Área: ${selectedArea.nombre}`   : '',
        selectedLinea?.nombre ? `Línea: ${selectedLinea.nombre}` : '',
    ].filter(Boolean).join('   |   ');
    if (infoText) {
        ws.mergeCells('A1:E1');
        ws.getCell('A1').value = infoText;
        ws.getCell('A1').style = {
            font: { size: 11 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(infoBg) } },
        };
        ws.getRow(1).height = 18;
    }

    const hdrs = [hStyle('Periodo', headerBg), hStyle('OEE (%)', headerBg), hStyle('TEEP (%)', headerBg), hStyle('MTBF (min)', headerBg), hStyle('MTTR (min)', headerBg)];
    const headerRow = ws.getRow(infoText ? 2 : 1);
    hdrs.forEach((h, i) => {
        headerRow.getCell(i + 1).value = h.value;
        headerRow.getCell(i + 1).style = h.style;
    });
    headerRow.height = 20;
    [18, 14, 14, 14, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    const dataRow = infoText ? 3 : 2;
    indicadores.forEach((ind, i) => {
        const row = ws.getRow(dataRow + i);
        row.getCell(1).value = ind.periodo;
        row.getCell(2).value = ind.oee  != null ? parseFloat(ind.oee.toFixed(1))  : null;
        row.getCell(3).value = ind.teep != null ? parseFloat(ind.teep.toFixed(1)) : null;
        row.getCell(4).value = ind.mtbf != null ? parseFloat(ind.mtbf.toFixed(1)) : null;
        row.getCell(5).value = ind.mttr != null ? parseFloat(ind.mttr.toFixed(1)) : null;
        if (i % 2 !== 0) {
            row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F8FE' } }; });
        }
        row.height = 16;
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `indicadores_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function kpiIconClass(v) {
    if (v == null) return 'grey';
    if (v >= 75) return 'green';
    if (v >= 60) return 'yellow';
    return 'red';
}

export default function Porcentajes() {
    const {
        indicadores,
        dataU, setDataU,
        dataE, setDataE,
        dataMtbf, setDataMtbf,
        dataMttr, setDataMttr,
        dataX, setDataX,
        selectedArea, selectedLinea, setExportFn,
    } = useReportes();

    useEffect(() => {
        if (indicadores.length > 0) {
            const datX = [], oee = [], teep = [], mttr = [], mtbf = [];
            indicadores.forEach((ind) => {
                datX.push(ind.periodo);
                oee.push(parseFloat((ind.oee  ?? 0).toFixed(1)));
                teep.push(parseFloat((ind.teep ?? 0).toFixed(1)));
                mttr.push(ind.mttr);
                mtbf.push(ind.mtbf);
            });
            setDataE(oee);
            setDataU(teep);
            setDataMtbf(mtbf);
            setDataMttr(mttr);
            setDataX(datX);
        }
    }, [indicadores, setDataE, setDataMtbf, setDataMttr, setDataU, setDataX]);

    useEffect(() => {
        if (indicadores.length > 0) {
            setExportFn(() => () => exportarExcel(indicadores, selectedArea, selectedLinea));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [indicadores, selectedArea, selectedLinea, setExportFn]);

    // ─── KPIs ────────────────────────────────────────────────────────────────────
    const kpis = useMemo(() => {
        const last = indicadores.length > 0 ? indicadores[indicadores.length - 1] : null;
        return {
            oee:  last?.oee  ?? null,
            teep: last?.teep ?? null,
            mtbf: last?.mtbf ?? null,
            mttr: last?.mttr ?? null,
        };
    }, [indicadores]);

    // ─── Chart options ────────────────────────────────────────────────────────────
    const baseOptions = useMemo(() => ({
        chart: {
            type: 'line', height: 300,
            zoom: { enabled: false }, toolbar: { show: false },
            fontFamily: 'WorkSans',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 600,
                animateGradually: { enabled: true, delay: 100 },
                dynamicAnimation: { enabled: true, speed: 400 },
            },
        },
        dataLabels: { enabled: false },
        stroke: { width: [3, 3], curve: 'smooth' },
        markers: { size: [4, 4], hover: { sizeOffset: 6 } },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '13px', fontFamily: 'WorkSans' },
        xaxis: {
            categories: dataX,
            labels: { style: { fontFamily: 'WorkSans', fontSize: '12px', colors: '#5f606f' } },
        },
        grid: { borderColor: '#e9ecf0' },
        noData: {
            text: 'Sin datos para esta selección',
            align: 'center', verticalAlign: 'middle',
            style: { fontSize: '15px', fontFamily: 'WorkSans', color: '#5f606f' },
        },
    }), [dataX]);

    const optOee = useMemo(() => ({
        ...baseOptions,
        colors: ['#2ccb9e', '#6495ED'],
        yaxis: {
            min: 0, max: 100, tickAmount: 5,
            title: {
                text: '%',
                style: { fontFamily: 'WorkSans', fontSize: '13px', color: '#5f606f' },
            },
            labels: {
                formatter: (v) => `${v}%`,
                style: { fontFamily: 'WorkSans', fontSize: '12px', colors: '#5f606f' },
            },
        },
        tooltip: {
            shared: true, intersect: false,
            y: { formatter: (v) => `${v != null ? v.toFixed(1) : '-'}%` },
        },
    }), [baseOptions]);

    const optMtbfMttr = useMemo(() => ({
        ...baseOptions,
        colors: ['#fe424d', '#ffc800'],
        yaxis: {
            title: {
                text: 'min',
                style: { fontFamily: 'WorkSans', fontSize: '13px', color: '#5f606f' },
            },
            labels: {
                formatter: (v) => `${v != null ? v.toFixed(1) : 0} min`,
                style: { fontFamily: 'WorkSans', fontSize: '12px', colors: '#5f606f' },
            },
        },
        tooltip: {
            shared: true, intersect: false,
            y: { formatter: (v) => `${v != null ? v.toFixed(1) : '-'} min` },
        },
    }), [baseOptions]);

    const seriesOee      = [{ name: 'OEE',  data: dataE }, { name: 'TEEP', data: dataU }];
    const seriesMtbfMttr = [{ name: 'MTTR', data: dataMttr }, { name: 'MTBF', data: dataMtbf }];

    const hasKpis = kpis.oee != null || kpis.teep != null || kpis.mtbf != null || kpis.mttr != null;

    return (
        <div className="porcentajes-container">

            {/* KPI row */}
            {hasKpis && (
                <div className="pareto-kpi-row">
                    {kpis.oee != null && (
                        <div className="pareto-kpi-card">
                            <div className={`pareto-kpi-card-icon pareto-kpi-card-icon--${kpiIconClass(kpis.oee)}`}>
                                <Gauge size={20} strokeWidth={2} />
                            </div>
                            <div className="pareto-kpi-card-body">
                                <span className="pareto-kpi-card-value">{kpis.oee.toFixed(1)} %</span>
                                <span className="pareto-kpi-card-label">OEE</span>
                            </div>
                        </div>
                    )}
                    {kpis.teep != null && (
                        <div className="pareto-kpi-card">
                            <div className={`pareto-kpi-card-icon pareto-kpi-card-icon--${kpiIconClass(kpis.teep)}`}>
                                <Gauge size={20} strokeWidth={2} />
                            </div>
                            <div className="pareto-kpi-card-body">
                                <span className="pareto-kpi-card-value">{kpis.teep.toFixed(1)} %</span>
                                <span className="pareto-kpi-card-label">TEEP</span>
                            </div>
                        </div>
                    )}
                    {kpis.mtbf != null && (
                        <div className="pareto-kpi-card">
                            <div className="pareto-kpi-card-icon pareto-kpi-card-icon--blue">
                                <Clock size={20} strokeWidth={2} />
                            </div>
                            <div className="pareto-kpi-card-body">
                                <span className="pareto-kpi-card-value">{kpis.mtbf.toFixed(1)} min</span>
                                <span className="pareto-kpi-card-label">MTBF</span>
                            </div>
                        </div>
                    )}
                    {kpis.mttr != null && (
                        <div className="pareto-kpi-card">
                            <div className="pareto-kpi-card-icon pareto-kpi-card-icon--red">
                                <Clock size={20} strokeWidth={2} />
                            </div>
                            <div className="pareto-kpi-card-body">
                                <span className="pareto-kpi-card-value">{kpis.mttr.toFixed(1)} min</span>
                                <span className="pareto-kpi-card-label">MTTR</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* OEE + TEEP chart */}
            <div className="tiempos-chart-card">
                <p className="tiempos-chart-title">OEE y TEEP</p>
                <Chart options={optOee} series={seriesOee} type="line" width="100%" height={300} />
            </div>

            {/* MTBF + MTTR chart */}
            <div className="tiempos-chart-card">
                <p className="tiempos-chart-title">MTBF y MTTR</p>
                <Chart options={optMtbfMttr} series={seriesMtbfMttr} type="line" width="100%" height={300} />
            </div>

        </div>
    );
}
